import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Heart, LayoutDashboard, Menu, PackageSearch, ShoppingBag } from 'lucide-react'
import { Logo } from '@/components/ui/Logo'
import { useCart } from '@/context/CartContext'
import { useWishlist } from '@/context/WishlistContext'
import { useAuth } from '@/context/AuthContext'
import { useSettings } from '@/context/SettingsContext'
import { useCategoryTree } from '@/hooks/useCategoryTree'
import { ROUTES } from '@/constants'
import { cn } from '@/utils/cn'
import { SearchBar } from '@/components/ui/SearchBar'
import { MobileMenu } from './MobileMenu'
import { CategoryNav } from './CategoryNav'
import { BottomNav } from './BottomNav'

/**
 * Storefront header: logo, a prominent always-visible search bar, and the
 * utility links (track order, wishlist, cart), with the category strip
 * underneath. Solid white everywhere — the old transparent-over-hero variant
 * went with the gradient hero it sat on.
 *
 * On phones the same pieces stack into two rows: menu · logo · wishlist ·
 * cart, then a full-width search field. Search stays visible rather than
 * hiding behind a toggle — on a shop it is the thing people reach for first.
 * A tab bar along the bottom (BottomNav) carries Home / Shop / Cart / Track
 * / Menu, and Track Order also lives in the drawer.
 */
export function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false)

  const { cartCount, openDrawer } = useCart()
  const { count: wishlistCount } = useWishlist()
  const { isStaff } = useAuth()
  const { brandName } = useSettings()
  const categoryTree = useCategoryTree()

  return (
    <>
      <header className="sticky top-0 z-30 border-b border-charcoal-100 bg-white/95 shadow-sm backdrop-blur">
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-2 px-3 sm:h-16 sm:gap-3 sm:px-6 lg:h-20 lg:gap-6 lg:px-8">
          <button
            type="button"
            aria-label="Open menu"
            onClick={() => setMenuOpen(true)}
            className="-ml-1 grid h-11 w-11 flex-shrink-0 place-items-center rounded-full text-charcoal hover:bg-sand-200 md:hidden"
          >
            <Menu size={24} />
          </button>

          <Link to={ROUTES.home} className="flex min-w-0 flex-shrink items-center gap-2 font-bold text-charcoal">
            <Logo size={40} className="sm:hidden" />
            <Logo size={44} className="hidden sm:inline-block" />
            <span className="hidden truncate text-base tracking-tight min-[360px]:inline sm:text-lg">
              {brandName}
            </span>
          </Link>

          {/* Search — the centrepiece on desktop; its own row on phones. */}
          <div className="hidden flex-1 justify-center px-2 md:flex">
            <SearchBar className="h-12 w-full max-w-2xl bg-sand-50" />
          </div>

          <div className="ml-auto flex flex-shrink-0 items-center gap-0.5 sm:gap-1 md:ml-0">
            <UtilityLink
              to={ROUTES.trackOrder}
              label="Track Order"
              Icon={PackageSearch}
              className="hidden sm:flex"
            />

            {/*
              One way in, not one per screen: the admin section has its own
              sidebar, so deep-linking individual admin pages from the shopper's
              navbar just duplicates it.
            */}
            {isStaff && (
              <UtilityLink
                to={ROUTES.adminDashboard}
                label="Admin"
                Icon={LayoutDashboard}
                className="hidden sm:flex"
              />
            )}

            <UtilityLink to={ROUTES.wishlist} label="Wishlist" Icon={Heart} count={wishlistCount} />

            <button
              type="button"
              aria-label="Open cart"
              onClick={openDrawer}
              className="relative flex min-h-11 min-w-11 flex-col items-center justify-center gap-0.5 rounded-lg px-2 py-1.5 text-charcoal transition hover:bg-sand-200 sm:px-3"
            >
              <span className="relative">
                <ShoppingBag size={22} />
                {cartCount > 0 && <CountBadge n={cartCount} />}
              </span>
              <span className="hidden text-[11px] font-medium lg:block">Cart</span>
            </button>
          </div>
        </div>

        <div className="px-3 pb-2.5 md:hidden">
          <SearchBar className="h-10 w-full bg-sand-50" />
        </div>

        <CategoryNav tree={categoryTree} />
      </header>
      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} tree={categoryTree} />
      <BottomNav onOpenMenu={() => setMenuOpen(true)} />
    </>
  )
}

function UtilityLink({ to, label, Icon, count = 0, className }) {
  return (
    <Link
      to={to}
      aria-label={label}
      className={cn(
        'relative flex min-h-11 min-w-11 flex-col items-center justify-center gap-0.5 rounded-lg px-2 py-1.5 text-charcoal transition hover:bg-sand-200 sm:px-3',
        className
      )}
    >
      <span className="relative">
        <Icon size={22} />
        {count > 0 && <CountBadge n={count} />}
      </span>
      <span className="hidden text-[11px] font-medium lg:block">{label}</span>
    </Link>
  )
}

function CountBadge({ n }) {
  return (
    <span
      className={cn(
        'absolute -right-2 -top-2 grid h-5 min-w-[20px] place-items-center rounded-full bg-coral px-1 text-[10px] font-bold text-white'
      )}
    >
      {n}
    </span>
  )
}
